import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import SimpleMarkdown from './SimpleMarkdown';

const DEBOUNCE_MS = 600;

function fmtLessonWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function fmtDeadline(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function CommentIcon({ kind, text }) {
  if (!text) return null;
  const label = kind === 'teacher' ? 'Комментарий преподавателя' : 'Комментарий ученика';
  return (
    <span
      className={`lesson-journal-comment-icon lesson-journal-comment-icon--${kind}`}
      tabIndex={0}
      aria-label={label}
    >
      <span className="lesson-journal-comment-glyph" aria-hidden>
        {kind === 'teacher' ? '💬' : '🗒'}
      </span>
      <span className="lesson-journal-comment-tip" role="tooltip">
        {text}
      </span>
    </span>
  );
}

function ReportGroupLine({ group, onSelectTaskSlug }) {
  return (
    <div className="lesson-journal-report-line">
      <span className="lesson-journal-exam-label">{group.label}:</span>
      <span className="lesson-journal-report-items">
        {(group.items || []).map((item) => {
          const clickable = Boolean(onSelectTaskSlug && item.task_slug);
          const inner = (
            <>
              <span className="lesson-journal-task-num">{item.display_number}</span>
              <span className="lesson-journal-mark">{item.mark || '!'}</span>
              {item.has_self_comment ? (
                <CommentIcon kind="student" text={item.self_comment} />
              ) : null}
              {item.has_teacher_comment ? (
                <CommentIcon kind="teacher" text={item.teacher_comment} />
              ) : null}
            </>
          );
          return (
            <span key={item.attempt_id || `${item.task_id}-${item.display_number}`} className="lesson-journal-task-chip">
              {clickable ? (
                <button
                  type="button"
                  className="link-like lesson-journal-task-btn"
                  title={item.task_title || item.task_slug}
                  onClick={() => onSelectTaskSlug(item.task_slug)}
                >
                  {inner}
                </button>
              ) : (
                <span className="lesson-journal-task-static" title={item.task_title || ''}>
                  {inner}
                </span>
              )}
            </span>
          );
        })}
      </span>
    </div>
  );
}

function Section({ title, children, className = '' }) {
  if (!children) return null;
  return (
    <section className={`lesson-journal-section ${className}`.trim()}>
      {title ? <h4 className="lesson-journal-section-title">{title}</h4> : null}
      <div className="lesson-journal-section-body">{children}</div>
    </section>
  );
}

function HomeworkItemRow({ item, onMarkRead, canMarkRead }) {
  const kindClass = `lesson-journal-hw-kind lesson-journal-hw-kind--${item.kind || 'task_list'}`;
  const href = item.href || '#';
  const isKonspekt = item.kind === 'konspekt';
  const numbers = Array.isArray(item.task_numbers) ? item.task_numbers.filter(Boolean) : [];

  return (
    <li className="lesson-journal-hw-item">
      <span className={kindClass}>{item.kind_label || item.kind}</span>
      <Link to={href} className="link-like lesson-journal-hw-link">
        {item.title || 'Без названия'}
      </Link>
      {numbers.length > 0 ? (
        <span className="text-secondary lesson-journal-hw-numbers">
          {numbers.map((line) => (
            <span key={line} className="lesson-journal-hw-numbers-line">{line}</span>
          ))}
        </span>
      ) : null}
      {item.status_label ? (
        <span className="text-secondary lesson-journal-hw-status">{item.status_label}</span>
      ) : null}
      {item.deadline_at ? (
        <span className="text-secondary lesson-journal-hw-deadline">
          до {fmtDeadline(item.deadline_at)}
        </span>
      ) : null}
      {canMarkRead && isKonspekt && onMarkRead ? (
        <label className="lesson-journal-hw-read">
          <input
            type="checkbox"
            checked={Boolean(item.read_at)}
            onChange={() => onMarkRead(item)}
          />
          <span>{item.read_at ? 'прочитал' : 'прочитал?'}</span>
        </label>
      ) : null}
    </li>
  );
}

function HomeworkList({ items, title, onMarkRead, canMarkRead, className = '' }) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return null;
  return (
    <div className={`lesson-journal-hw ${className}`.trim()}>
      {title ? <div className="lesson-journal-note-label">{title}</div> : null}
      <ul className="lesson-journal-hw-list">
        {list.map((item) => (
          <HomeworkItemRow
            key={item.id}
            item={item}
            onMarkRead={onMarkRead}
            canMarkRead={canMarkRead}
          />
        ))}
      </ul>
    </div>
  );
}

function QuizReportRow({ quiz }) {
  if (!quiz) return null;
  const total = Number(quiz.total) || 0;
  const correct = Number(quiz.correct) || 0;
  const unchecked = Number(quiz.unchecked) || 0;
  const href = quiz.href || (quiz.task_list_id ? `/quiz-review/${quiz.task_list_id}` : '#');
  let summary = `${correct} из ${total} верно`;
  if (unchecked > 0) {
    summary += `, ${unchecked} без проверки`;
  }
  return (
    <div className="lesson-journal-quiz-row">
      <span className="lesson-journal-hw-kind lesson-journal-hw-kind--quiz">Тест</span>
      <span className="lesson-journal-quiz-title">{quiz.title || 'Тест'}</span>
      <span className="lesson-journal-quiz-summary">{summary}</span>
      <Link to={href} className="link-like lesson-journal-quiz-link">
        разобрать
      </Link>
    </div>
  );
}

function QuizReportList({ quizzes }) {
  const list = Array.isArray(quizzes) ? quizzes : [];
  if (list.length === 0) return null;
  return (
    <div className="lesson-journal-quiz-list">
      {list.map((quiz) => (
        <QuizReportRow key={quiz.task_list_id || quiz.homework_item_id} quiz={quiz} />
      ))}
    </div>
  );
}

function billingTexts(reports) {
  const list = Array.isArray(reports) ? reports : [];
  const topics = [];
  const descriptions = [];
  const homeworks = [];
  for (const r of list) {
    if (r.topic?.trim()) topics.push(r.topic.trim());
    if (r.description?.trim()) descriptions.push(r.description.trim());
    if (r.homework?.trim()) homeworks.push(r.homework.trim());
  }
  return { topics, descriptions, homeworks };
}

function OrphanBillingCard({ report, mode }) {
  const { topics, descriptions, homeworks } = billingTexts([report]);
  return (
    <li className="lesson-journal-card lesson-journal-card--billing-only is-ended">
      <header className="lesson-journal-card-head">
        <div>
          <strong>{fmtLessonWhen(report.created_at)}</strong>
          <span className="badge session-badge lesson-journal-billing-badge">Из биллинга</span>
        </div>
        {mode === 'student' || mode === 'parent' ? (
          report.teacher_username ? (
            <span className="text-secondary">с @{report.teacher_username}</span>
          ) : null
        ) : null}
      </header>
      {topics.length > 0 ? (
        <div className="lesson-journal-card-title">
          {topics.map((t) => (
            <p key={t} className="lesson-journal-card-title-text">{t}</p>
          ))}
        </div>
      ) : null}
      {descriptions.length > 0 ? (
        <Section title="На занятии">
          {descriptions.map((d) => (
            <p key={d} className="lesson-journal-note-text">{d}</p>
          ))}
        </Section>
      ) : null}
      {homeworks.length > 0 ? (
        <Section title="Домашнее задание">
          {homeworks.map((h) => (
            <SimpleMarkdown key={h} className="lesson-journal-note-text" text={h} />
          ))}
        </Section>
      ) : null}
      {topics.length === 0 && descriptions.length === 0 && homeworks.length === 0 ? (
        <p className="text-secondary">В биллинге нет текстовых заметок.</p>
      ) : null}
    </li>
  );
}

/**
 * Журнал занятий (LessonSession): даты, ДЗ, компактный отчёт, заметки.
 *
 * mode:
 * - 'teacher' — GET /teacher/students/:id/lesson-sessions/, без правки student_notes
 * - 'student' — GET /tasks/lesson-sessions/?journal=1, можно править student_notes
 * - 'parent' — GET /parent/children/:id/lesson-sessions/, только чтение
 */
export default function LessonJournal({
  mode = 'student',
  studentId = null,
  onSelectTaskSlug = null,
  refreshNonce = 0,
}) {
  const [sessions, setSessions] = useState([]);
  const [orphanReports, setOrphanReports] = useState([]);
  const [currentTodo, setCurrentTodo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notesDrafts, setNotesDrafts] = useState({});
  const [notesSaved, setNotesSaved] = useState({});
  const [savingNotes, setSavingNotes] = useState({});
  const debounceRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let res;
      if (mode === 'teacher') {
        res = await api.get(`/teacher/students/${studentId}/lesson-sessions/`);
      } else if (mode === 'parent') {
        res = await api.get(`/parent/children/${studentId}/lesson-sessions/`);
      } else {
        res = await api.get('/tasks/lesson-sessions/', { params: { journal: 1 } });
      }
      const data = res.data;
      const list = Array.isArray(data)
        ? data
        : (data?.sessions ?? data?.results ?? []);
      const orphans = Array.isArray(data)
        ? []
        : (data?.orphan_billing_reports ?? []);
      const todo = Array.isArray(data) ? [] : (data?.current_todo ?? []);
      setSessions(list);
      setOrphanReports(orphans);
      setCurrentTodo(todo);
      if (mode === 'student') {
        const drafts = {};
        for (const s of list) {
          drafts[s.id] = s.student_notes || '';
        }
        setNotesDrafts(drafts);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось загрузить журнал занятий.';
      setError(typeof msg === 'string' ? msg : 'Не удалось загрузить журнал занятий.');
      setSessions([]);
      setOrphanReports([]);
      setCurrentTodo([]);
    } finally {
      setLoading(false);
    }
  }, [mode, studentId]);

  useEffect(() => {
    if ((mode === 'teacher' || mode === 'parent') && !studentId) return;
    void load();
  }, [load, mode, studentId, refreshNonce]);

  useEffect(() => () => {
    Object.values(debounceRefs.current).forEach((t) => clearTimeout(t));
  }, []);

  const saveStudentNotes = useCallback(async (sessionId, text) => {
    setSavingNotes((prev) => ({ ...prev, [sessionId]: true }));
    try {
      await api.patch(`/tasks/lesson-sessions/${sessionId}/`, { student_notes: text });
      setSessions((prev) => prev.map((s) => (
        s.id === sessionId ? { ...s, student_notes: text } : s
      )));
      setNotesSaved((prev) => ({ ...prev, [sessionId]: true }));
      setTimeout(() => {
        setNotesSaved((prev) => ({ ...prev, [sessionId]: false }));
      }, 1500);
    } catch {
      setError('Не удалось сохранить комментарий к занятию.');
    } finally {
      setSavingNotes((prev) => ({ ...prev, [sessionId]: false }));
    }
  }, []);

  const onStudentNotesChange = (sessionId, text) => {
    setNotesDrafts((prev) => ({ ...prev, [sessionId]: text }));
    if (debounceRefs.current[sessionId]) {
      clearTimeout(debounceRefs.current[sessionId]);
    }
    debounceRefs.current[sessionId] = setTimeout(() => {
      void saveStudentNotes(sessionId, text);
    }, DEBOUNCE_MS);
  };

  const onMarkRead = useCallback(async (item) => {
    if (mode !== 'student' || !item?.id) return;
    try {
      const res = await api.post(`/tasks/homework-items/${item.id}/read/`, {
        read: !item.read_at,
      });
      const updated = res.data;
      const patch = (list) => (list || []).map((row) => (
        row.id === updated.id ? { ...row, ...updated } : row
      ));
      setCurrentTodo((prev) => patch(prev));
      setSessions((prev) => prev.map((s) => ({
        ...s,
        homework: patch(s.homework),
        homework_long_term: patch(s.homework_long_term),
        homework_report: s.homework_report
          ? {
              ...s.homework_report,
              other_items: patch(s.homework_report.other_items),
            }
          : s.homework_report,
      })));
    } catch {
      setError('Не удалось обновить отметку «прочитал».');
    }
  }, [mode]);

  if (loading) {
    return <p className="text-secondary">Загрузка журнала занятий…</p>;
  }

  const empty = sessions.length === 0 && orphanReports.length === 0 && currentTodo.length === 0;
  const canMarkRead = mode === 'student';

  return (
    <div className="lesson-journal">
      {error ? <div className="alert alert-error">{error}</div> : null}

      {currentTodo.length > 0 ? (
        <section className="lesson-journal-current-todo">
          <h3 className="lesson-journal-current-todo-title">Сейчас сделать</h3>
          <HomeworkList
            items={currentTodo}
            onMarkRead={onMarkRead}
            canMarkRead={canMarkRead}
          />
        </section>
      ) : null}

      {empty ? (
        <p className="text-secondary">Пока нет занятий в журнале.</p>
      ) : (
        <ul className="lesson-journal-list">
          {sessions.map((session) => {
            const groups = Array.isArray(session.report_groups) ? session.report_groups : [];
            const ended = Boolean(session.ended_at);
            const billing = Array.isArray(session.billing_reports) ? session.billing_reports : [];
            const homework = Array.isArray(session.homework) ? session.homework : [];
            const longTerm = Array.isArray(session.homework_long_term)
              ? session.homework_long_term
              : [];
            const { topics, descriptions, homeworks: billingHw } = billingTexts(billing);
            const hwReport = session.homework_report || {};
            const hwGroups = Array.isArray(hwReport.groups) ? hwReport.groups : [];
            const hwOther = Array.isArray(hwReport.other_items) ? hwReport.other_items : [];
            const hwQuizzes = Array.isArray(hwReport.quizzes) ? hwReport.quizzes : [];
            const hasHwReportContent = hwGroups.length > 0 || hwOther.length > 0 || hwQuizzes.length > 0;
            const showStudentNotes = mode === 'student' || Boolean(session.student_notes?.trim());
            const showHwReportSection = hasHwReportContent || showStudentNotes;
            const showInClass = (
              descriptions.length > 0
              || Boolean(session.teacher_notes?.trim())
              || groups.length > 0
            );
            const showHomework = billingHw.length > 0 || homework.length > 0 || longTerm.length > 0;

            return (
              <li key={session.id} className={`lesson-journal-card ${ended ? 'is-ended' : 'is-active'}`}>
                <header className="lesson-journal-card-head">
                  <div>
                    <strong>{fmtLessonWhen(session.started_at)}</strong>
                    {ended ? (
                      <span className="text-secondary lesson-journal-end">
                        {' '}— {fmtLessonWhen(session.ended_at)}
                      </span>
                    ) : (
                      <span className="badge session-badge lesson-journal-live">Идёт</span>
                    )}
                  </div>
                  {(mode === 'student' || mode === 'parent') && session.teacher_username ? (
                    <span className="text-secondary">с @{session.teacher_username}</span>
                  ) : null}
                </header>

                {topics.length > 0 ? (
                  <div className="lesson-journal-card-title">
                    {topics.map((t) => (
                      <p key={t} className="lesson-journal-card-title-text">{t}</p>
                    ))}
                  </div>
                ) : null}

                {showHwReportSection ? (
                  <Section title="Отчёт по ДЗ">
                    {hwGroups.length > 0 ? (
                      <div className="lesson-journal-report">
                        {hwGroups.map((group) => (
                          <ReportGroupLine
                            key={`${session.id}-hw-${group.label}`}
                            group={group}
                            onSelectTaskSlug={onSelectTaskSlug}
                          />
                        ))}
                      </div>
                    ) : null}
                    <QuizReportList quizzes={hwQuizzes} />
                    <HomeworkList
                      items={hwOther}
                      onMarkRead={onMarkRead}
                      canMarkRead={canMarkRead}
                    />
                    {showStudentNotes ? (
                      <div className="lesson-journal-note-block lesson-journal-hw-comment">
                        <div className="lesson-journal-note-label">
                          Комментарий ученика
                          {mode === 'student' && savingNotes[session.id] ? (
                            <span className="text-secondary"> · сохранение…</span>
                          ) : null}
                          {mode === 'student' && notesSaved[session.id] ? (
                            <span className="text-secondary"> · сохранено</span>
                          ) : null}
                        </div>
                        {mode === 'student' ? (
                          <textarea
                            className="form-textarea lesson-journal-notes-input"
                            rows={3}
                            value={notesDrafts[session.id] ?? session.student_notes ?? ''}
                            onChange={(e) => onStudentNotesChange(session.id, e.target.value)}
                            placeholder="Комментарий к отчёту по ДЗ…"
                          />
                        ) : (
                          <p className="lesson-journal-note-text">{session.student_notes}</p>
                        )}
                      </div>
                    ) : null}
                  </Section>
                ) : null}

                {showInClass ? (
                  <Section title="На занятии">
                    {descriptions.map((d) => (
                      <p key={d} className="lesson-journal-note-text">{d}</p>
                    ))}
                    {session.teacher_notes?.trim() ? (
                      <div className="lesson-journal-note-block">
                        <div className="lesson-journal-note-label">Комментарий преподавателя</div>
                        <p className="lesson-journal-note-text">{session.teacher_notes}</p>
                      </div>
                    ) : null}
                    {groups.length > 0 ? (
                      <div className="lesson-journal-report">
                        {groups.map((group) => (
                          <ReportGroupLine
                            key={`${session.id}-${group.label}`}
                            group={group}
                            onSelectTaskSlug={onSelectTaskSlug}
                          />
                        ))}
                      </div>
                    ) : null}
                  </Section>
                ) : null}

                {showHomework ? (
                  <Section title="Домашнее задание">
                    {billingHw.map((h) => (
                      <SimpleMarkdown key={h} className="lesson-journal-note-text" text={h} />
                    ))}
                    <HomeworkList
                      items={homework}
                      onMarkRead={onMarkRead}
                      canMarkRead={canMarkRead}
                    />
                    <HomeworkList
                      items={longTerm}
                      title="Длительные задания"
                      onMarkRead={onMarkRead}
                      canMarkRead={canMarkRead}
                      className="lesson-journal-hw--footer"
                    />
                  </Section>
                ) : null}
              </li>
            );
          })}
          {orphanReports.map((report) => (
            <OrphanBillingCard key={`orphan-${report.id}`} report={report} mode={mode} />
          ))}
        </ul>
      )}
      <button type="button" className="btn btn-xs btn-outline" onClick={() => void load()} disabled={loading}>
        Обновить
      </button>
    </div>
  );
}
